import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { waLink } from "@/lib/whatsapp";
import { INSTAGRAM } from "@/lib/social";

const ParallaxImage = ({ src, alt, testid, className = "" }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
    return (
        <div ref={ref} className={`overflow-hidden ${className}`}>
            <motion.img
                src={src}
                alt={alt}
                data-testid={testid}
                style={{ y, scale: 1.18 }}
                loading="lazy"
                className="w-full h-full object-cover transition-[filter] duration-700"
            />
        </div>
    );
};

const Formations = ({ items, dark }) => (
    <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {items.map((f, i) => (
            <li
                key={f}
                className={`text-[11px] tracking-[0.25em] uppercase flex items-center gap-2 ${
                    dark ? "text-white/60" : "text-[#0A0A0A]/60"
                }`}
            >
                <span className={`w-1 h-1 rounded-full ${i === 0 ? "bg-[#6A2022]" : dark ? "bg-white/30" : "bg-[#0A0A0A]/30"}`} />
                {f}
            </li>
        ))}
    </ul>
);

const Experiences = () => (
    <section id="experiencias" data-testid="experiences-section" className="bg-white">
        <div className="px-6 md:px-12 pt-28 md:pt-40 pb-20 md:pb-28 max-w-7xl mx-auto">
            <Reveal>
                <p className="text-[10px] tracking-[0.45em] uppercase text-[#6A2022] mb-8" data-testid="experiences-label">
                    Nossas Experiências
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[0.95] tracking-tight max-w-4xl" data-testid="experiences-headline">
                    Escolha como você quer viver{" "}
                    <span className="font-editorial italic font-light normal-case text-[#6A2022]">
                        esse momento.
                    </span>
                </h2>
            </Reveal>
        </div>

        {/* LC CERIMONIAL — bloco claro */}
        <div id="cerimonial" data-testid="cerimonial-block" className="bg-white text-[#0A0A0A] border-t border-black/10">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                <div className="md:col-span-7 order-1">
                    <Reveal>
                        <ParallaxImage
                            src="/media/lc-cerimonial.webp"
                            alt="LC Cerimonial — músicos de cordas em cerimônia de casamento"
                            testid="cerimonial-image"
                            className="aspect-[4/5] md:aspect-[5/6]"
                        />
                    </Reveal>
                </div>
                <div className="md:col-span-5 order-2">
                    <Reveal>
                        <p className="text-[10px] tracking-[0.45em] uppercase text-[#6A2022] mb-6">Experiência 01</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight" data-testid="cerimonial-title">
                            LC Cerimonial
                        </h3>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="font-editorial italic font-light text-2xl md:text-3xl mt-4 text-[#6A2022]" data-testid="cerimonial-subtitle">
                            A trilha sonora do momento que muda tudo.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 text-sm md:text-base text-[#0A0A0A]/70 leading-relaxed" data-testid="cerimonial-description">
                            Formações musicais personalizadas para transformar sua cerimônia em uma
                            experiência emocionante, elegante e inesquecível.
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <div className="mt-8 pt-6 border-t border-[#0A0A0A]/15">
                            <Formations items={["Solo", "Duo", "Trio", "Quarteto", "Formações personalizadas"]} />
                        </div>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href={INSTAGRAM.cerimonial}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="cerimonial-instagram"
                                className="inline-flex items-center gap-3 bg-[#6A2022] text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors duration-300 hover:bg-[#7d282b]"
                            >
                                <Instagram className="w-4 h-4" />
                                Conhecer LC Cerimonial
                            </a>
                            <a
                                href={waLink("cerimonial")}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="cerimonial-cta"
                                className="inline-flex items-center gap-3 border border-[#0A0A0A] px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors duration-300 hover:bg-[#6A2022] hover:border-[#6A2022] hover:text-white"
                            >
                                Solicitar orçamento
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>

        {/* ASTROLIVE — bloco escuro */}
        <div id="astrolive" data-testid="astrolive-block" className="bg-[#0A0A0A]">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                <div className="md:col-span-5 order-2 md:order-1">
                    <Reveal>
                        <p className="text-[10px] tracking-[0.45em] uppercase text-[#B06465] mb-6">Experiência 02</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white" data-testid="astrolive-title">
                            AstroLive
                        </h3>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="font-editorial italic font-light text-2xl md:text-3xl mt-4 text-[#B06465]" data-testid="astrolive-subtitle">
                            A pista virou show.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-6 text-sm md:text-base text-white/60 leading-relaxed" data-testid="astrolive-description">
                            DJ, música ao vivo, performance e interação transformados em uma experiência
                            criada para elevar a energia da sua festa.
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href={INSTAGRAM.astrolive}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="astrolive-instagram"
                                className="inline-flex items-center gap-3 bg-[#6A2022] text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors duration-300 hover:bg-[#7d282b]"
                            >
                                <Instagram className="w-4 h-4" />
                                Conhecer AstroLive
                            </a>
                            <a
                                href={waLink("astrolive")}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="astrolive-cta"
                                className="inline-flex items-center gap-3 border border-white/40 text-white px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors duration-300 hover:bg-[#6A2022] hover:border-[#6A2022]"
                            >
                                Solicitar orçamento
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </div>
                    </Reveal>
                </div>
                <div className="md:col-span-7 order-1 md:order-2">
                    <Reveal>
                        <ParallaxImage
                            src="/media/astrolive.webp"
                            alt="AstroLive — saxofonista no meio da pista com convidados"
                            testid="astrolive-image"
                            className="aspect-[4/5] md:aspect-[5/6]"
                        />
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

export default Experiences;
